import { useCallback } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import {
  createFilterNode,
  createFilterOption,
  csvImportNewNode,
  getFiltersByRoadmapId,
} from '@/features/canvas/api';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useImportInitiativeContext } from '@/features/canvas/contexts/import-initiative-context';
import { FilterOptionValue } from '@/features/canvas/types/filter-option';
import { InitiativeFieldValues, InitiativeToBeImported } from '@/features/canvas/types/initiative';
import { components } from '@/types/api';

interface UseImportedInitiativeDropProps {
  roadmapId: number;
}

export const useCSVImportedInitiativeDrop = ({ roadmapId }: UseImportedInitiativeDropProps) => {
  const { updateLastSavedTime, filters } = useEditorContext();
  const { budgetFieldsToBeCreatedFromImport } = useImportInitiativeContext();
  const queryClient = useQueryClient();

  const { mutateAsync: createFilterOptionMutation } = useMutation(
    ({ filterId, payload }: { filterId: number; payload: components['schemas']['FilterOption'] }) =>
      createFilterOption({ filterId, payload }),
    { onSuccess: () => updateLastSavedTime() }
  );
  const { mutateAsync: createFilterNodeMutation } = useMutation(
    ({ filterId, payload }: { filterId: number; payload: components['schemas']['FilterNode'] }) =>
      createFilterNode({ filterId, payload }),
    { onSuccess: () => updateLastSavedTime() }
  );

  const { mutateAsync: csvImportNewNodeMutation } = useMutation(
    ({ csvNode }: { csvNode: components['schemas']['NodeImportCSV'] }) =>
      csvImportNewNode({ roadmapId, csvNode }),
    {
      onSuccess: () => updateLastSavedTime(),
    }
  );

  const createFilterOptions = useCallback(
    async ({
      filter,
      newlyCreatedNode,
      filterItemToImport,
    }: {
      filter: components['schemas']['Filter'];
      filterItemToImport: FilterOptionValue;
      newlyCreatedNode: components['schemas']['NodeImportCSV'];
    }) => {
      const matchingOption = filter.options.find(fo => fo.title === filterItemToImport.value);

      if (!matchingOption) {
        const newOption = await createFilterOptionMutation({
          filterId: filter.id!,
          payload: { color: '', title: filterItemToImport.value },
        });
        const newFilterNode = await createFilterNodeMutation({
          filterId: filter.id!,
          payload: { nodeId: newlyCreatedNode.id!, optionId: newOption.id! },
        });
        return newFilterNode;
      }
      const newFilterNode = await createFilterNodeMutation({
        filterId: filter.id!,
        payload: { nodeId: newlyCreatedNode.id!, optionId: matchingOption.id! },
      });

      return newFilterNode;
    },
    [createFilterNodeMutation, createFilterOptionMutation]
  );

  const onCSVImportedInitiativeDrop = useCallback(
    async ({
      initiative,
      newInitiativeFieldValues,
      newInitiativeFilterValues,
      newRoadmapNode,
    }: {
      initiative: InitiativeToBeImported;
      newRoadmapNode: components['schemas']['NodeImportCSV'];
      newInitiativeFieldValues?: InitiativeFieldValues[];
      newInitiativeFilterValues?: FilterOptionValue[];
    }) => {
      const { id, ...nodeWithoutId } = newRoadmapNode;

      // Create mapping for budget fields
      const budgetFieldMapping = new Map<string, string>();
      budgetFieldsToBeCreatedFromImport.forEach(budgetField => {
        const hasValue = newInitiativeFieldValues?.find(fv => fv.id === budgetField.id);
        if (hasValue) {
          budgetFieldMapping.set(budgetField.id, budgetField.title);
        }
      });

      // Separate budget fields from regular fields
      const budgetFields: typeof newInitiativeFieldValues = [];
      const regularFields: typeof newInitiativeFieldValues = [];

      [...(newInitiativeFieldValues || [])].forEach(fv => {
        if (fv.isBudgetField) {
          budgetFields.push(fv);
        } else {
          regularFields.push(fv);
        }
      });

      // Process budget fields to use titles instead of temporary IDs
      const processedBudgetFields = budgetFields
        .map(fv => {
          const { id: fieldId, ...fieldWithoutId } = fv;
          const budgetFieldTitle = budgetFieldMapping.get(fieldId);
          if (budgetFieldTitle) {
            return {
              ...fieldWithoutId,
              title: budgetFieldTitle,
              type: 'big', // Budget fields are always big/multi-line
              isBudgetField: true,
            };
          }
          return null;
        })
        .filter((field): field is NonNullable<typeof field> => field !== null);

      // Process regular fields (remove id and isBudgetField flag)
      const fields = regularFields.map(fv => {
        const { id: fieldId, isBudgetField, ...fieldWithoutId } = fv;
        return fieldWithoutId;
      });

      const fieldsWithoutDuplicates = fields.reduce((accumulator, currentValue) => {
        const { title, type, value } = currentValue;
        const existingItem = accumulator.find(item => item.title === title && item.type === type);
        if (existingItem) {
          if (value !== undefined) {
            existingItem.value = value;
          }
        } else {
          accumulator.push(currentValue);
        }
        return accumulator;
      }, [] as typeof fields);

      // Build the CSV node
      const csvNode: components['schemas']['NodeImportCSV'] & {
        initiatives: {
          title: string;
          budget?: { currency: string; total: number };
          existing_id?: number;
          fields?: { title: string; type: string; value: string; isBudgetField?: boolean }[];
          fields_to_update?: string[];
        };
      } = {
        ...nodeWithoutId,
        initiatives: {
          title: initiative.title,
        },
      };

      // Add existing ID and fields to update for re-imports
      if (initiative.existingInitiativeId) {
        csvNode.initiatives.existing_id = initiative.existingInitiativeId;
      }
      if (initiative.fieldsToUpdate && initiative.fieldsToUpdate.length > 0) {
        csvNode.initiatives.fields_to_update = initiative.fieldsToUpdate;
      }

      // Combine all fields
      const allFields = [
        ...fieldsWithoutDuplicates.map(f => ({ ...f, isBudgetField: false })),
        ...processedBudgetFields.map(bf => ({
          title: bf.title,
          type: bf.type,
          value: bf.value,
          isBudgetField: true,
        })),
      ];

      // Add fields to CSV node
      if (allFields.length > 0) {
        csvNode.initiatives.fields = allFields;
      }

      // Add budget to the payload if present (including sub-fields)
      if (initiative.budgetValue) {
        const budget: {
          currency: string;
          capex?: number;
          opex?: number;
          spend?: number;
          total?: number;
        } = {
          currency: initiative.budgetValue.currency || 'GBP',
        };

        if (initiative.budgetValue.total !== undefined && initiative.budgetValue.total !== null) {
          budget.total = Number(initiative.budgetValue.total);
        }
        if (initiative.budgetValue.capex !== undefined && initiative.budgetValue.capex !== null) {
          budget.capex = Number(initiative.budgetValue.capex);
        }
        if (initiative.budgetValue.opex !== undefined && initiative.budgetValue.opex !== null) {
          budget.opex = Number(initiative.budgetValue.opex);
        }
        if (initiative.budgetValue.spend !== undefined && initiative.budgetValue.spend !== null) {
          budget.spend = Number(initiative.budgetValue.spend);
        }

        // Only add budget if at least one value is present
        if (
          budget.total !== undefined ||
          budget.capex !== undefined ||
          budget.opex !== undefined ||
          budget.spend !== undefined
        ) {
          csvNode.initiatives.budget = budget as { currency: string; total: number };
        }
      }

      const newlyCreatedNode = await csvImportNewNodeMutation({ csvNode });

      if (newInitiativeFilterValues?.length) {
        const newFilterOptionsAndNodesPromises = newInitiativeFilterValues.map(async ifv => {
          const filter = filters.find(f => f.id === Number(ifv.id))!;
          if (filter.selection === 'multiple') {
            const splitItems = ifv.value.split(',').map(i => i.trim());
            const splitOptionsPromises = splitItems.map(async splitItem => {
              const filterNode = await createFilterOptions({
                filter,
                newlyCreatedNode,
                filterItemToImport: { ...ifv, value: splitItem },
              });
              return filterNode;
            });
            await Promise.all(splitOptionsPromises);
          } else {
            const filterNode = await createFilterOptions({
              filter,
              newlyCreatedNode,
              filterItemToImport: ifv,
            });
            return filterNode;
          }
        });
        await Promise.all(newFilterOptionsAndNodesPromises);
        await queryClient.invalidateQueries([getFiltersByRoadmapId.name, roadmapId]);
      }
    },
    [
      createFilterOptions,
      csvImportNewNodeMutation,
      filters,
      queryClient,
      roadmapId,
      budgetFieldsToBeCreatedFromImport,
    ]
  );

  return {
    onCSVImportedInitiativeDrop,
  };
};
